import React, { useState } from 'react'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { toast } from 'react-toastify'
import { isEmpty } from 'lodash'

import HamburgerIcon from './HamburgerIcon'
import Avatar from './Avatar'
import ConfirmNotification from './ConfirmNotification'
import Button from './Button'
import Dialog from './dialog/Dialog'

import networkConnected from '../utils/networkConnected'
import DeviceSubtypes from '../utils/deviceSubtypes'

import { refreshConfig } from '../actions/config'
import { logout } from '../actions/attendant'
import { onSafIsUploading } from '../actions/peripheral'

import { getDeviceSubtype } from '../selectors/config'
import { hasCartItems } from '../selectors/cart'
import { getAttendants, makeGetActiveAttendant } from '../selectors/attendant'
import { getIsKiosk } from '../selectors/menus'
import { getSafIsUploading } from '../selectors/peripheral'

import styles from './NavMenu.module.scss'

const NavMenu = ({ history, deviceSubtype, cartItemsExist, onToggle, logout, attendantName, attendantsExist, refreshConfig, className, isKiosk, safIsUploading, setSafIsUploading }) => {
  const classNames = cn(styles.navMenu, className)
  const [toastMessage, setToastMessage] = useState({ show: false, id: null })
  const [confirmLogout, setConfirmLogout] = useState(false)
  const [hamburgerClickedWhileSyncing, setHamburgerClickedWhileSyncing] = useState(false)

  let logoutContent = (
    <>
      <Avatar className={styles.avatar} />
      <span className={styles.name}>{attendantName}</span>
      <span className={styles.logoutText}>{attendantName ? 'Logout' : 'Login'}</span>
    </>
  )

  const onClickLogout = () => {
      logout()
      history.replace('/user-login')
  }

  const handleLoginLogout = () => {
    if (!confirmLogout && attendantName) {
      setConfirmLogout(true)
    } else if (!attendantName) {
        logout()
        history.replace('/user-login')
    }
  }

  if (confirmLogout) {
    logoutContent = (
      <div className={styles.logoutConfirmation}>
        <p>Are you sure you want to logout?</p>
        <Button onClick={() => setConfirmLogout(false)}>No</Button>
        <Button className={styles.confirmLogoutButton} onClick={onClickLogout}>YES</Button>
      </div>
    )
  }

  let logoutContainer = null

  if (attendantsExist) {
    logoutContainer = (
      <div className={styles.logout} onClick={handleLoginLogout}>
        {logoutContent}
      </div>
    )
  }

  const onClickMenu = () => {
    history.push('/concession-order')
    return onToggle()
  }

  const onClickOrders = () => {
    isKiosk
      ? history.push('/menuSelection')
      : history.push('/orders')
    return onToggle()
  }

  const onClickConfiguration = () => {
    history.push({
      pathname: '/revenue-center-setup',
      state: { prevLocation: history.location.pathname}
    })
    return onToggle()
  }

  const onClickSyncNetworkData = () => {
    networkConnected().then(() => {
      window.location = `${window.location.href}?cacheBuster=${new Date().toISOString()}`
    })
  }

  const onClickDebugInfo = () => {
    history.replace('/debug-info')
    return onToggle()
  }

  const onClickKiosk = () => {
    history.replace('/kiosk/attract_loop')
    return onToggle()
  }


  const warnNotification = (notification) => {
    const toastId = toast(notification, {
      autoClose: false,
      closeOnClick: false,
      className: styles.toastContainer,
      position: "top-center"
    })
    setToastMessage({ show: true, id: toastId })
  }

  const toggleMessages = () => {
    toast.dismiss(toastMessage.id)
    setToastMessage({ show: false, id: null })
  }

  const checkCartBeforeConfirm = (onConfirm) => {
    const notification = (
      <ConfirmNotification
        className={className}
        message={
          'Items in cart will be removed upon syncing or navigating away from the menu. Do you wish to continue?'
        }
        onCancel={toggleMessages}
        onConfirm={onConfirm}
        buttonOneText={'Cancel'}
        buttonTwoText={'Abandon Cart'}
      />
    )
    if (cartItemsExist) {
      warnNotification(notification)
    } else {
      onConfirm()
    }
  }

  const onClickRefreshConfig = (event) => {
    event.preventDefault()
    event.stopPropagation()
    checkCartBeforeConfirm(() => {
      refreshConfig()
      onToggle()
    })
  }

  const onHamburgerClick = () => {
    if (safIsUploading) {
      setHamburgerClickedWhileSyncing(true)
      return
    }

    onToggle()
  }

  const onStopSyncClick = () => {
    setSafIsUploading(false)
    setHamburgerClickedWhileSyncing(false)
  }

  const stopSyncMessage = "These functions are not supported while a sync is in progress.  Stopping the sync will leave some orders unsynced."

  return (
    <div className={classNames}>
      <div className={styles.sidebarContainer}>
        <div className={styles.header}>
          <div></div>
          <button className={styles.menuToggle} onClick={onHamburgerClick}>
            <HamburgerIcon className={styles.menuToggleIcon} />
          </button>
        </div>
        {hamburgerClickedWhileSyncing && (
          <Dialog message={stopSyncMessage}>
              <div className={styles.actionsContainer}>
                <button className={styles.buttonContinue} onClick={() => setHamburgerClickedWhileSyncing(false)}>Continue Sync</button>
                <button className={styles.buttonCancel} onClick={onStopSyncClick}>Stop Sync</button>
              </div>
          </Dialog>
        )}
        <div className={styles.links}>
          {DeviceSubtypes.isConcessions(deviceSubtype) && <div onClick={onClickMenu} title='Menu'>Menu</div>}
          {isKiosk && <div onClick={onClickKiosk} title='Kiosk'>Kiosk</div>}

          <div onClick={onClickOrders} title='Orders'>Orders</div>
          <div onClick={onClickConfiguration} title='Configuration'>Configuration</div>
          <div onClick={onClickRefreshConfig} title='Refresh Config'>Refresh Config</div>
          <div onClick={onClickSyncNetworkData} title='Sync Network Data'>Sync Network Data</div>
          <div onClick={onClickDebugInfo} title='Debug Info'>Debug Info</div>
        </div>
        {logoutContainer}
      </div>
      <div className={styles.overlay} onClick={() => onToggle()} />
    </div>
  )
}

NavMenu.propTypes = {
  onToggle: PropTypes.func,
}

const mapStateToProps = (state) => {
  const deviceSubtype = getDeviceSubtype(state)
  const cartItemsExist = hasCartItems(state)
  const attendantsExist = !isEmpty(getAttendants(state))
  const getActiveAttendant = makeGetActiveAttendant()
  const activeAttendant = getActiveAttendant(state)
  const isKiosk = getIsKiosk(state)
  const safIsUploading = getSafIsUploading(state)

  return {
    deviceSubtype,
    cartItemsExist,
    attendantName: activeAttendant?.name ?? '',
    attendantsExist,
    isKiosk,
    safIsUploading,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    refreshConfig: () => dispatch(refreshConfig()),
    logout: () => dispatch(logout()),
    setSafIsUploading: (isUploading) => dispatch(onSafIsUploading(isUploading)),
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(NavMenu))
